import {
  BufferGeometry,
  CapsuleGeometry,
  CylinderGeometry,
  DodecahedronGeometry,
} from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

import { CHARACTER_PRESENTATION_CONFIG } from "../../config/character.ts";
import type { Footwear, LowerGarment } from "../../sim/appearance.ts";

export interface LowerBodyGeometrySet {
  readonly legs: Readonly<Record<LowerGarment, BufferGeometry>>;
  readonly footwear: Readonly<Record<Footwear, BufferGeometry>>;
}

export function footwearBottomHeight(
  geometry: BufferGeometry,
  legPivotHeightMeters: number,
  footwearLocalY: number,
  footwearLocalZ: number,
  legAngleRadians: number,
  statureScale = 1,
  limbScale = 1,
): number {
  if (geometry.boundingBox === null) geometry.computeBoundingBox();
  const bounds = geometry.boundingBox;
  if (bounds === null) {
    throw new Error("Footwear geometry requires finite bounds");
  }
  const centerY = footwearLocalY + (bounds.min.y + bounds.max.y) * 0.5;
  const centerZ = footwearLocalZ + (bounds.min.z + bounds.max.z) * 0.5;
  const halfY = (bounds.max.y - bounds.min.y) * 0.5;
  const halfZ = (bounds.max.z - bounds.min.z) * 0.5;
  const cosine = Math.cos(legAngleRadians);
  const sine = Math.sin(legAngleRadians);
  const rotatedCenterY = legPivotHeightMeters +
    centerY * cosine - centerZ * limbScale * sine;
  const verticalExtent = halfY * Math.abs(cosine) +
    halfZ * limbScale * Math.abs(sine);
  return (rotatedCenterY - verticalExtent) * statureScale;
}

function hangingCylinder(
  lengthMeters: number,
  topRadiusMeters: number,
  bottomRadiusMeters: number,
): BufferGeometry {
  const geometry = new CylinderGeometry(
    topRadiusMeters,
    bottomRadiusMeters,
    lengthMeters,
    6,
  );
  geometry.translate(0, -lengthMeters * 0.5, 0);
  return geometry;
}

function merge(
  geometries: readonly BufferGeometry[],
  name: string,
): BufferGeometry {
  const merged = mergeGeometries([...geometries], false);
  if (merged === null) {
    throw new Error("Could not merge " + name + " lower-body geometry");
  }
  merged.name = name;
  for (const geometry of geometries) geometry.dispose();
  return merged;
}

function shoeCapsule(
  radius: number,
  length: number,
  name: string,
): BufferGeometry {
  const geometry = new CapsuleGeometry(radius, length, 2, 7);
  geometry.rotateX(Math.PI * 0.5);
  geometry.name = name;
  return geometry;
}

export function createLowerBodyGeometrySet(
  legLengthMeters: number,
): LowerBodyGeometrySet {
  const widthScale = CHARACTER_PRESENTATION_CONFIG.proportions.legWidthScale;
  const bootScale = CHARACTER_PRESENTATION_CONFIG.proportions.bootScale;
  const legs = {
    trousers: hangingCylinder(
      legLengthMeters,
      0.105 * widthScale,
      0.078 * widthScale,
    ),
    wide_trousers: hangingCylinder(
      legLengthMeters,
      0.121 * widthScale,
      0.113 * widthScale,
    ),
    track_pants: hangingCylinder(
      legLengthMeters,
      0.1 * widthScale,
      0.066 * widthScale,
    ),
    skirt: hangingCylinder(
      legLengthMeters,
      0.096 * widthScale,
      0.069 * widthScale,
    ),
  } satisfies Record<LowerGarment, BufferGeometry>;
  for (const [id, geometry] of Object.entries(legs)) {
    geometry.name = "lower:" + id;
  }

  const footwear = {
    walking_boots: merge([
      shoeCapsule(0.071 * bootScale, 0.15 * bootScale, "walking-boot-toe"),
      (() => {
        const ankle = new CylinderGeometry(
          0.079 * bootScale,
          0.073 * bootScale,
          0.13 * bootScale,
          7,
        );
        ankle.translate(0, 0.055 * bootScale, -0.055 * bootScale);
        return ankle;
      })(),
    ], "footwear:walking_boots"),
    trainers: (() => {
      const geometry = new DodecahedronGeometry(0.115 * bootScale, 0);
      geometry.scale(0.83, 0.42, 1.48);
      geometry.translate(0, -0.01 * bootScale, 0.055 * bootScale);
      geometry.name = "footwear:trainers";
      return geometry;
    })(),
    dress_shoes: (() => {
      const geometry = new DodecahedronGeometry(0.115 * bootScale, 0);
      geometry.scale(0.68, 0.33, 1.28);
      geometry.translate(0, -0.018 * bootScale, 0.04 * bootScale);
      geometry.name = "footwear:dress_shoes";
      return geometry;
    })(),
    work_boots: merge([
      shoeCapsule(0.079 * bootScale, 0.16 * bootScale, "work-boot-toe"),
      (() => {
        const ankle = new CylinderGeometry(
          0.09 * bootScale,
          0.081 * bootScale,
          0.2 * bootScale,
          7,
        );
        ankle.translate(0, 0.085 * bootScale, -0.06 * bootScale);
        return ankle;
      })(),
    ], "footwear:work_boots"),
  } satisfies Record<Footwear, BufferGeometry>;

  return { legs, footwear };
}

export function disposeLowerBodyGeometrySet(
  geometries: Readonly<LowerBodyGeometrySet>,
): void {
  for (const geometry of [
    ...Object.values(geometries.legs),
    ...Object.values(geometries.footwear),
  ]) {
    geometry.dispose();
  }
}
